import { useApolloClient } from '@apollo/client';
import {
  type MutableRefObject,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  type Cutsheet,
  type ProcessorCapability,
  type ProcessorSettings,
  ViewSchedulingEditDocument,
  type ViewSchedulingEditQuery,
} from 'lib/graphql';

/**
 * Manages cutsheet wizard data fetching and state.
 *
 * Stores query results in refs (outside Apollo cache) to prevent
 * re-renders and cache collisions with other scheduling queries.
 */
export function useWizardData() {
  const apolloClient = useApolloClient();
  const wizardDataRef = useRef<ViewSchedulingEditQuery | null>(null);
  const [wizardDataReady, setWizardDataReady] = useState(false);
  const templateCutsheetRef = useRef<Cutsheet | undefined>(undefined);

  /**
   * Fetches wizard config (capabilities + settings) without marking ready.
   * Use when you need to await additional parallel work before showing the wizard.
   */
  const fetchWizardConfig = useCallback(
    async (jobId: string, userId: string) => {
      const { data } = await apolloClient.query<ViewSchedulingEditQuery>({
        query: ViewSchedulingEditDocument,
        variables: { jobId, userId, isDeleted: false },
        fetchPolicy: 'no-cache',
      });
      wizardDataRef.current = data;
      return data;
    },
    [apolloClient],
  );

  /**
   * Fetches wizard config and marks data as ready when complete.
   * Use for simple open-wizard flows where no parallel work is needed.
   */
  const fetchWizardDataAndSetReady = useCallback(
    async (jobId: string, userId: string) => {
      setWizardDataReady(false);
      await fetchWizardConfig(jobId, userId);
      setWizardDataReady(true);
    },
    [fetchWizardConfig],
  );

  // wizardDataReady is intentionally used as the dep to recompute when the ref is populated.
  const wizardCapabilities = useMemo(
    () =>
      (wizardDataRef.current?.findProcessorSchedulingById
        ?.processorCapabilities as ProcessorCapability[]) ?? [],
    [wizardDataReady],
  );
  const wizardSettings = useMemo(
    () =>
      wizardDataRef.current?.findProcessorSchedulingById
        ?.processorSettings as ProcessorSettings,
    [wizardDataReady],
  );

  return {
    wizardDataReady,
    setWizardDataReady,
    wizardCapabilities,
    wizardSettings,
    templateCutsheetRef: templateCutsheetRef as MutableRefObject<
      Cutsheet | undefined
    >,
    fetchWizardConfig,
    fetchWizardDataAndSetReady,
  };
}
